// Small browser-session helpers: remembered lobby (survives a page reload in
// this tab) and the ?authError=oauth notice from a failed OAuth callback.

const LOBBY_KEY = "wp.activeLobby";
const LOBBY_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

type SessionStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function tabStorage(): SessionStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadActiveLobby(store: SessionStore | null = tabStorage()): string | null {
  try {
    const id = store?.getItem(LOBBY_KEY) ?? null;
    return id !== null && LOBBY_ID_RE.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function saveActiveLobby(id: string | null, store: SessionStore | null = tabStorage()): void {
  try {
    if (id === null) store?.removeItem(LOBBY_KEY);
    else if (LOBBY_ID_RE.test(id)) store?.setItem(LOBBY_KEY, id);
  } catch {
    // storage blocked: reload just returns to the menu
  }
}

export const OAUTH_ERROR_MESSAGE =
  "Signing in with Discord or Google didn't work. Please try again, or use a username and password.";

/** Reads ?authError=oauth and returns the cleaned URL (or null when unchanged). */
export function takeAuthError(href: string): { message: string | null; cleanUrl: string | null } {
  const url = new URL(href);
  const code = url.searchParams.get("authError");
  if (code === null) return { message: null, cleanUrl: null };
  const step = url.searchParams.get("step");
  url.searchParams.delete("authError");
  url.searchParams.delete("step");
  const base = code === "oauth" ? OAUTH_ERROR_MESSAGE : "Sign-in failed. Please try again.";
  const message = step && /^[a-z_]{1,20}$/.test(step) ? `${base} (Error: ${step})` : base;
  return { message, cleanUrl: `${url.pathname}${url.search}${url.hash}` };
}
